/**
 * Personel Anlık Alacak Widget'ı — Faz 5.5: Manuel hareketler eklendi.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import {
  Wallet, ChevronDown, ChevronUp, Clock, Coffee,
  Award, CreditCard, Plus, Minus, Activity, Calendar, Sparkles, Cake,
  AlertTriangle, LogOut, XCircle, Info, FilePlus, FileMinus,
} from 'lucide-react'
import { buAyMaasHesapla, BONUS_GUNLUK_CAP_DK, type MaasHesabi } from '@/lib/maas/maas-hesap'
import { icerideMi } from '@/lib/services/kayit-service'
import type { KullaniciPersonel } from '@/types'

interface Props {
  personel: KullaniciPersonel
}

function lokalIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function AlacakKarti({ personel }: Props) {
  const [hesap, setHesap] = useState<MaasHesabi | null>(null)
  const [yukleniyor, setYukleniyor] = useState(true)
  const [detayAcik, setDetayAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [aktifMi, setAktifMi] = useState(false)

  const baseTimestampRef = useRef<number>(Date.now())
  const [gecenSn, setGecenSn] = useState<number>(0)

  const yukle = useCallback(async () => {
    try {
      const [h, icer] = await Promise.all([
        buAyMaasHesapla(personel),
        icerideMi(personel.id),
      ])
      setHesap(h)
      setAktifMi(icer)
      baseTimestampRef.current = Date.now()
      setGecenSn(0)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Hesaplanamadı.')
    } finally {
      setYukleniyor(false)
    }
  }, [personel])

  useEffect(() => { yukle() }, [yukle])
  useEffect(() => {
    const interval = setInterval(yukle, 30000)
    return () => clearInterval(interval)
  }, [yukle])

  useEffect(() => {
    if (!aktifMi || !hesap) return
    if (hesap.saatlikKarsilik <= 0) return
    const interval = setInterval(() => {
      setGecenSn((Date.now() - baseTimestampRef.current) / 1000)
    }, 200)
    return () => clearInterval(interval)
  }, [aktifMi, hesap])

  if (yukleniyor && !hesap) {
    return (
      <div className="card text-center py-6">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
      </div>
    )
  }

  if (hata || !hesap) {
    return (
      <div className="card bg-red-50 border-red-200 text-center py-4">
        <p className="text-sm text-red-800">{hata ?? 'Veri yok'}</p>
      </div>
    )
  }

  const saatlik = hesap.saatlikKarsilik
  // Tavan aşıldıysa (yetkisiz) canlı sayaç durur; yetkiliyse ek mesaiye akar
  const ekSaat = aktifMi && !hesap.acikAralikTavanAsti ? gecenSn / 3600 : 0
  const ekDk = ekSaat * 60

  // Normal mesai (tavana kadar) ve ek mesai (yetkili, tavan üstü) ayrı
  const normalSaatBaz = hesap.normalMesaiDakika / 60
  const ekMesaiSaatBaz = hesap.ekMesaiDakika / 60
  // Canlı sayaç dağıtımı: tavan aşılmadıysa normale, yetkili+aşıldıysa ek mesaiye
  let canliNormalSaat = normalSaatBaz
  let canliEkMesaiSaat = ekMesaiSaatBaz
  if (hesap.acikAralikEkMesaide) {
    canliEkMesaiSaat += ekSaat
  } else {
    canliNormalSaat += ekSaat
  }
  const normalMesaiBaz = canliNormalSaat * saatlik
  const ekMesaiBaz = canliEkMesaiSaat * saatlik
  const yansitilanToplamSaat = canliNormalSaat + canliEkMesaiSaat
  const yansitilanAnlikBaz = normalMesaiBaz + ekMesaiBaz

  const bugunIso = lokalIso(new Date())

  const bugunTatil = aktifMi
    ? hesap.tatilCalismalari.find((t) => t.tarih === bugunIso)
    : undefined
  let ekTatilBonus = 0
  if (bugunTatil) {
    const kalanBonusDk = Math.max(0, BONUS_GUNLUK_CAP_DK - bugunTatil.bonusDakika)
    const eklenecekDk = Math.min(ekDk, kalanBonusDk)
    ekTatilBonus = (eklenecekDk / 60) * saatlik * (bugunTatil.efektifKatsayi - 1)
  }
  const yansitilanTatilBonus = hesap.tatilBonusToplam + ekTatilBonus

  const bugunDogum = aktifMi && hesap.dogumGunuCalisma?.tarih === bugunIso
    ? hesap.dogumGunuCalisma
    : null
  let ekDogumBonus = 0
  if (bugunDogum) {
    const kalanBonusDk = Math.max(0, BONUS_GUNLUK_CAP_DK - bugunDogum.bonusDakika)
    const eklenecekDk = Math.min(ekDk, kalanBonusDk)
    ekDogumBonus = (eklenecekDk / 60) * saatlik * (bugunDogum.katsayi - 1)
  }
  const yansitilanDogumBonus = hesap.dogumGunuBonus + ekDogumBonus

  const ekKesintiler =
    -hesap.molaAsanKesinti
    - hesap.izinMaasKesinti
    + hesap.izinAlacagi
    + hesap.yillikIzinOdemesi
    + yansitilanTatilBonus
    + yansitilanDogumBonus
    + hesap.puantajToplam
    - hesap.odenmisAvansToplam
    + hesap.sabitEkOdeme
    - hesap.sabitKesinti
    - hesap.eksikMesaiKesinti
    - hesap.gecGelmeCezasi
    - hesap.noShowCezasi
    + hesap.manuelHareketNet
  const yansitilanAnlikNet = yansitilanAnlikBaz + ekKesintiler

  const saatler = Math.floor(yansitilanToplamSaat)
  const dakika = Math.floor((yansitilanToplamSaat - saatler) * 60)
  const saniye = Math.floor((((yansitilanToplamSaat - saatler) * 60) - dakika) * 60)

  const gecCezaSayisi = Math.max(0, hesap.gecGelmeSayisi - 2)
  const kalanGecHaki = Math.max(0, 2 - hesap.gecGelmeSayisi)

  const tatilCapDoldu = bugunTatil
    ? bugunTatil.bonusDakika + Math.min(ekDk, BONUS_GUNLUK_CAP_DK - bugunTatil.bonusDakika) >= BONUS_GUNLUK_CAP_DK
    : false

  return (
    <div className="space-y-3">
      {hesap.gecGelmeSayisi > 0 && hesap.gecGelmeSayisi < 3 && (
        <UyariBandi
          tip="bilgi"
          ikon={<Info className="w-5 h-5" />}
          baslik={`Bu ay ${hesap.gecGelmeSayisi} kez geç geldin`}
          mesaj={`${kalanGecHaki} kez daha geç gelirsen 3'üncüden itibaren her geç gelme için ${paraTam(hesap.gecCezaBirimTL)} ₺ ceza uygulanır.`}
        />
      )}
      {hesap.gecGelmeSayisi >= 3 && (
        <UyariBandi
          tip="ceza"
          ikon={<AlertTriangle className="w-5 h-5" />}
          baslik={`⚠ Bu ay ${hesap.gecGelmeSayisi} kez geç geldin`}
          mesaj={`3'üncüden itibaren her geç gelme için ${paraTam(hesap.gecCezaBirimTL)} ₺ ceza uygulandı. Toplam kesinti: ${paraTam(hesap.gecGelmeCezasi)} ₺`}
        />
      )}
      {hesap.noShowSayisi > 0 && (
        <UyariBandi
          tip="ceza"
          ikon={<XCircle className="w-5 h-5" />}
          baslik={`Bu ay ${hesap.noShowSayisi} vardiyaya gelmedin`}
          mesaj={`Her vardiya için 1 günlük mesai bedeli (${paraTam(hesap.noShowCezaBirimTL)} ₺) ceza uygulandı. Toplam: ${paraTam(hesap.noShowCezasi)} ₺`}
        />
      )}

      <div className="card bg-gradient-to-br from-brand-500 to-indigo-600 border-0 text-white">
        <div className="flex items-center gap-2 mb-2">
          <Wallet className="w-5 h-5" />
          <h2 className="font-bold text-sm uppercase tracking-wide opacity-90 flex-1">
            Bu Ay Şu Ana Kadar Alacağın
          </h2>
          {aktifMi && (
            <span className="text-[10px] px-1.5 py-0.5 bg-white/20 rounded-full inline-flex items-center gap-0.5">
              <Activity className="w-2.5 h-2.5 animate-pulse" />
              canlı
            </span>
          )}
        </div>

        <p className="text-4xl font-bold tabular-nums">
          {aktifMi ? paraKurus(yansitilanAnlikNet) : paraTam(yansitilanAnlikNet)}
          <span className="text-2xl ml-1 opacity-80">₺</span>
        </p>

        <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
          <div className="bg-white/15 rounded-lg px-2 py-1.5">
            <div className="opacity-75">Çalışma</div>
            <div className="font-semibold tabular-nums">
              {saatler}s {String(dakika).padStart(2, '0')}dk
              {aktifMi && <span className="opacity-70"> {String(saniye).padStart(2, '0')}sn</span>}
            </div>
          </div>
          <div className="bg-white/15 rounded-lg px-2 py-1.5">
            <div className="opacity-75">Saat ücreti</div>
            <div className="font-semibold">
              {paraTam(saatlik)} ₺
            </div>
          </div>
        </div>

        <button
          onClick={() => setDetayAcik((v) => !v)}
          className="mt-3 w-full flex items-center justify-center gap-1 py-2 bg-white/15 hover:bg-white/25 rounded-lg text-xs font-semibold transition-colors"
        >
          {detayAcik ? (
            <><ChevronUp className="w-4 h-4" /> Detayı Gizle</>
          ) : (
            <><ChevronDown className="w-4 h-4" /> Detayı Gör</>
          )}
        </button>

        {detayAcik && (
          <div className="mt-3 bg-white/10 rounded-xl p-3 space-y-1.5 text-sm">
            <DetaySatir
              ikon={<Clock className="w-3.5 h-3.5" />}
              etiket={`Anlık mesai: ${canliNormalSaat.toFixed(2)} sa × ${paraTam(saatlik)} ₺`}
              tutar={normalMesaiBaz}
              tip="arti"
              kurus={aktifMi}
            />

            {canliEkMesaiSaat > 0.001 && (
              <DetaySatir
                ikon={<Sparkles className="w-3.5 h-3.5" />}
                etiket={`Ek mesai: ${canliEkMesaiSaat.toFixed(2)} sa × ${paraTam(saatlik)} ₺`}
                tutar={ekMesaiBaz}
                tip="arti"
                kurus={aktifMi && hesap.acikAralikEkMesaide}
              />
            )}

            {hesap.izinAlacagi > 0 && (
              <DetaySatir
                ikon={<Calendar className="w-3.5 h-3.5" />}
                etiket={`İzin alacağı (${hesap.izinHakki} gün × 10 sa hakkı)`}
                tutar={hesap.izinAlacagi}
                tip="arti"
              />
            )}

            {hesap.yillikIzinOdemesi > 0 && (
              <DetaySatir
                ikon={<Calendar className="w-3.5 h-3.5" />}
                etiket={`Yıllık izin alacağı (${hesap.yillikIzinGun} gün × 10 sa)`}
                tutar={hesap.yillikIzinOdemesi}
                tip="arti"
              />
            )}

            {hesap.molaAsanDakika > 0 && (
              <DetaySatir
                ikon={<Coffee className="w-3.5 h-3.5" />}
                etiket={`Mola aşımı (${hesap.molaAsanDakika}dk)`}
                tutar={-hesap.molaAsanKesinti}
                tip="eksi"
              />
            )}

            {hesap.izinMaasGunToplam > 0 && (
              <DetaySatir
                ikon={<Calendar className="w-3.5 h-3.5" />}
                etiket={`İzin maaş kesintisi (${hesap.izinMaasGunToplam} gün)`}
                tutar={-hesap.izinMaasKesinti}
                tip="eksi"
              />
            )}

            {hesap.tatilBonusToplam > 0 && (
              <>
                <DetaySatir
                  ikon={<Sparkles className="w-3.5 h-3.5" />}
                  etiket={`Resmi tatil bonusu (${hesap.tatilCalismalari.length} gün, max 10 sa/gün)`}
                  tutar={yansitilanTatilBonus}
                  tip="arti"
                  kurus={aktifMi && !!bugunTatil && !tatilCapDoldu}
                />
                <div className="ml-5 mt-0.5 space-y-0.5 text-[10px] opacity-80">
                  {hesap.tatilCalismalari.map((t) => {
                    const canliMi = t.tarih === bugunIso && aktifMi
                    let tBonus = t.bonus
                    if (canliMi) tBonus += ekTatilBonus
                    const capInfo = t.dakika > BONUS_GUNLUK_CAP_DK
                      ? ` (${(t.dakika / 60).toFixed(1)}sa çalışıldı, ilk 10sa)`
                      : ''
                    return (
                      <div key={t.tarih} className="flex justify-between">
                        <span className="truncate mr-2">
                          {t.tatilAdi} · x{t.efektifKatsayi}{capInfo}
                        </span>
                        <span className="tabular-nums">
                          +{canliMi && !tatilCapDoldu ? paraKurus(tBonus) : paraTam(tBonus)} ₺
                        </span>
                      </div>
                    )
                  })}
                </div>
              </>
            )}

            {hesap.dogumGunuBonus > 0 && hesap.dogumGunuCalisma && (
              <DetaySatir
                ikon={<Cake className="w-3.5 h-3.5" />}
                etiket={`🎂 Doğum günü bonusu (x${hesap.dogumGunuCalisma.katsayi}, max 10 sa)`}
                tutar={yansitilanDogumBonus}
                tip="arti"
                kurus={aktifMi && !!bugunDogum && ekDogumBonus > 0}
              />
            )}

            {hesap.eksikMesaiKesinti > 0 && (
              <DetaySatir
                ikon={<LogOut className="w-3.5 h-3.5" />}
                etiket={`Eksik mesai (${hesap.eksikMesaiDakika} dk)`}
                tutar={-hesap.eksikMesaiKesinti}
                tip="eksi"
              />
            )}
            {hesap.gecGelmeCezasi > 0 && (
              <DetaySatir
                ikon={<AlertTriangle className="w-3.5 h-3.5" />}
                etiket={`Geç gelme cezası (3'üncüden itibaren ${gecCezaSayisi} × ${paraTam(hesap.gecCezaBirimTL)} ₺)`}
                tutar={-hesap.gecGelmeCezasi}
                tip="eksi"
              />
            )}
            {hesap.noShowCezasi > 0 && (
              <DetaySatir
                ikon={<XCircle className="w-3.5 h-3.5" />}
                etiket={`No-show cezası (${hesap.noShowSayisi} vardiya × ${paraTam(hesap.noShowCezaBirimTL)} ₺)`}
                tutar={-hesap.noShowCezasi}
                tip="eksi"
              />
            )}

            {/* YENİ — Manuel hareketler */}
            {hesap.manuelEklemeToplam > 0 && (
              <>
                <DetaySatir
                  ikon={<FilePlus className="w-3.5 h-3.5" />}
                  etiket={`Maaşa ek (${hesap.manuelHareketler.filter((h) => h.tip === 'ekleme').length} adet)`}
                  tutar={hesap.manuelEklemeToplam}
                  tip="arti"
                />
                <div className="ml-5 mt-0.5 space-y-0.5 text-[10px] opacity-80">
                  {hesap.manuelHareketler
                    .filter((h) => h.tip === 'ekleme')
                    .map((h) => (
                      <div key={h.id} className="flex justify-between gap-2">
                        <span className="truncate mr-2">{h.aciklama}</span>
                        <span className="tabular-nums flex-shrink-0">+{paraTam(h.tutar)} ₺</span>
                      </div>
                    ))}
                </div>
              </>
            )}

            {hesap.manuelKesintiToplam > 0 && (
              <>
                <DetaySatir
                  ikon={<FileMinus className="w-3.5 h-3.5" />}
                  etiket={`Maaştan kesinti (${hesap.manuelHareketler.filter((h) => h.tip === 'kesinti').length} adet)`}
                  tutar={-hesap.manuelKesintiToplam}
                  tip="eksi"
                />
                <div className="ml-5 mt-0.5 space-y-0.5 text-[10px] opacity-80">
                  {hesap.manuelHareketler
                    .filter((h) => h.tip === 'kesinti')
                    .map((h) => (
                      <div key={h.id} className="flex justify-between gap-2">
                        <span className="truncate mr-2">{h.aciklama}</span>
                        <span className="tabular-nums flex-shrink-0">−{paraTam(h.tutar)} ₺</span>
                      </div>
                    ))}
                </div>
              </>
            )}

            {hesap.puantajToplam !== 0 && (
              <DetaySatir
                ikon={<Award className="w-3.5 h-3.5" />}
                etiket={`Puantaj (${hesap.puantajlar.length} işlem)`}
                tutar={hesap.puantajToplam}
                tip={hesap.puantajToplam > 0 ? 'arti' : 'eksi'}
              />
            )}

            {hesap.odenmisAvansToplam > 0 && (
              <DetaySatir
                ikon={<CreditCard className="w-3.5 h-3.5" />}
                etiket="Aldığın avans"
                tutar={-hesap.odenmisAvansToplam}
                tip="eksi"
              />
            )}

            {hesap.sabitEkOdeme > 0 && (
              <DetaySatir
                ikon={<Plus className="w-3.5 h-3.5" />}
                etiket="Sabit ek ödeme"
                tutar={hesap.sabitEkOdeme}
                tip="arti"
              />
            )}

            {hesap.sabitKesinti > 0 && (
              <DetaySatir
                ikon={<Minus className="w-3.5 h-3.5" />}
                etiket="Sabit kesinti"
                tutar={-hesap.sabitKesinti}
                tip="eksi"
              />
            )}

            <div className="border-t border-white/30 pt-2 mt-2">
              <div className="flex justify-between items-center font-bold">
                <span>NET</span>
                <span className="text-base tabular-nums">
                  {aktifMi ? paraKurus(yansitilanAnlikNet) : paraTam(yansitilanAnlikNet)} ₺
                </span>
              </div>
              {personel.maasTipi === 'aylik' && (
                <div className="flex justify-between items-center text-[11px] opacity-70 mt-1">
                  <span>Ay sonu tahmini</span>
                  <span>{paraTam(hesap.netMaas)} ₺</span>
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-[10px] opacity-60 mt-2 text-center">
          {aktifMi ? 'Mesaidesin • her saniye artıyor' : 'Mesai dışı • sabit'}
        </p>
      </div>
    </div>
  )
}

interface UyariBandiProps {
  tip: 'bilgi' | 'ceza'
  ikon: React.ReactNode
  baslik: string
  mesaj: string
}

function UyariBandi({ tip, ikon, baslik, mesaj }: UyariBandiProps) {
  const renkler = tip === 'bilgi'
    ? 'bg-amber-50 border-amber-300 text-amber-900'
    : 'bg-red-50 border-red-300 text-red-900'
  const ikonRenk = tip === 'bilgi' ? 'text-amber-600' : 'text-red-600'
  return (
    <div className={`rounded-xl border-2 p-3 flex items-start gap-2.5 ${renkler}`}>
      <div className={`flex-shrink-0 ${ikonRenk}`}>{ikon}</div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm">{baslik}</p>
        <p className="text-xs mt-0.5 opacity-90">{mesaj}</p>
      </div>
    </div>
  )
}

interface DetaySatirProps {
  ikon: React.ReactNode
  etiket: string
  tutar: number
  tip: 'arti' | 'eksi'
  kurus?: boolean
}

function DetaySatir({ ikon, etiket, tutar, tip, kurus = false }: DetaySatirProps) {
  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-1.5 opacity-90">
        {ikon}
        <span className="text-xs">{etiket}</span>
      </div>
      <span className={`font-semibold tabular-nums ${tip === 'arti' ? 'text-green-200' : 'text-red-200'}`}>
        {tip === 'arti' ? '+' : ''}
        {kurus ? paraKurus(tutar) : paraTam(tutar)} ₺
      </span>
    </div>
  )
}

function paraTam(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function paraKurus(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
